import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Building2, Car, Clock, RefreshCw, ShieldCheck, Loader2, Database, Zap, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBilling } from "@/context/BillingContext";

const nf = (n) => (n || n === 0 ? Number(n).toLocaleString() : "—");
const signed = (n) => (n > 0 ? `+${nf(n)}` : nf(n));
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
};

function DeltaBadge({ dod, trend7, testid }) {
  if (dod === null || dod === undefined) return null;
  const Icon = dod > 0 ? TrendingUp : dod < 0 ? TrendingDown : Minus;
  const color = dod > 0 ? "text-emerald-400" : dod < 0 ? "text-rose-400" : "text-muted-foreground";
  return (
    <div className={`mt-2 flex items-center gap-1.5 text-xs ${color}`} data-testid={testid}>
      <Icon className="h-3.5 w-3.5" />
      <span className="font-mono-data tabular-nums">{signed(dod)}</span>
      <span className="text-muted-foreground">today</span>
      {trend7 !== null && trend7 !== undefined && (
        <span className="ml-1 text-muted-foreground">· {signed(trend7)} 7d</span>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, testid, delay, delta }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay, duration: 0.4 }}
      data-testid={testid}
      className="relative overflow-hidden rounded-2xl border border-border/60 bg-card/60 p-6 backdrop-blur">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground">
        <Icon className="h-4 w-4 text-accent" /> {label}
      </div>
      <div className="mt-3 font-mono-data text-4xl font-semibold tabular-nums text-foreground">{value}</div>
      {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
      {delta && <DeltaBadge {...delta} testid={`${testid}-delta`} />}
    </motion.div>
  );
}

export default function Admin() {
  const { user, loading } = useAuth();
  const { ent } = useBilling();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  const [warming, setWarming] = useState(false);
  const [range, setRange] = useState(90);
  const [err, setErr] = useState(null);
  const [fresh, setFresh] = useState(null);

  const load = async () => {
    setBusy(true);
    try {
      const r = await api.get("/admin/corpus"); setData(r.data); setErr(null);
      api.get("/admin/freshness").then((fr) => setFresh(fr.data)).catch(() => {});
    }
    catch (e) { setErr(e?.response?.status === 403 ? "forbidden" : "error"); }
    finally { setBusy(false); }
  };

  const runWarm = async (force = false) => {
    if (force && !window.confirm("Force refresh will fetch fresh listings NOW, bypassing the once-per-day budget guard. Use this after fixing a provider key. Continue?")) return;
    setWarming(true);
    try {
      const r = await api.post(`/admin/warm${force ? "?force=true" : ""}`);
      if (r.data?.ran) {
        toast.success(r.data.message || "Warm started");
        setTimeout(load, 8000);
        setTimeout(load, 20000);
      } else {
        toast.info(r.data?.message || "Today's fetch already ran — budget preserved.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start warm");
    } finally { setWarming(false); }
  };

  useEffect(() => { if (user) load(); /* eslint-disable-next-line */ }, [user]);

  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  // Gate to admins only (billing status flag).
  if (ent && !ent.is_admin) return <Navigate to="/dashboard" replace />;

  const history = (data?.history || []).map((h) => ({ date: (h.date || "").slice(5), homes: h.homes || 0, vehicles: h.vehicles || 0 }));
  const chartData = history.slice(-range);
  // Day-over-day and ~7-day growth deltas from the daily snapshot history.
  const delta = (key) => {
    const h = data?.history || [];
    if (h.length < 2) return null;
    const latest = h[h.length - 1]?.[key] ?? 0;
    const prev = h[h.length - 2]?.[key] ?? 0;
    const weekAgo = h.length >= 8 ? h[h.length - 8]?.[key] : h[0]?.[key];
    return { dod: latest - prev, trend7: weekAgo === undefined ? null : latest - weekAgo };
  };

  return (
    <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6" data-testid="admin-corpus-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-accent">
            <ShieldCheck className="h-5 w-5" />
            <span className="text-xs uppercase tracking-[0.3em]">Admin · Corpus Watch</span>
          </div>
          <h1 className="mt-2 text-4xl font-semibold tracking-tight sm:text-5xl">Data Corpus</h1>
          <p className="mt-2 max-w-xl text-sm text-muted-foreground">
            Live counts and daily growth of the verified listing corpus. Fetched once daily at 6:00 AM ET — this panel reads the database only.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="default" size="sm" onClick={() => runWarm(false)} disabled={warming} data-testid="admin-warm-btn">
            {warming ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Zap className="mr-2 h-4 w-4" />} Run warm now
          </Button>
          <Button variant="outline" size="sm" onClick={() => runWarm(true)} disabled={warming} data-testid="admin-force-warm-btn">
            <Zap className="mr-2 h-4 w-4" /> Force refresh
          </Button>
          <Button variant="outline" size="sm" onClick={load} disabled={busy} data-testid="admin-refresh-btn">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />} Refresh
          </Button>
        </div>
      </div>

      {err === "forbidden" && (
        <div className="mt-8 rounded-xl border border-border/60 bg-card/60 p-6 text-sm text-muted-foreground" data-testid="admin-forbidden">
          You need an admin account to view this page.
        </div>
      )}

      {data && (
        <>
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard icon={Building2} label="Homes" value={nf(data.homes)} sub="verified $1M+ listings" testid="stat-homes" delay={0.02} delta={delta("homes")} />
            <StatCard icon={Car} label="Vehicles" value={nf(data.vehicles)} sub="verified $150K+ listings" testid="stat-vehicles" delay={0.06} delta={delta("vehicles")} />
            <StatCard icon={Clock} label="Last Warm" value={<span className="text-lg">{fmtDate(data.last_warm_at)}</span>} sub={`Provider fetch day: ${data.last_provider_warm_date || "—"}`} testid="stat-lastwarm" delay={0.1} />
            <StatCard icon={Database} label="Daily Budget" value={<span className="text-lg">{data.budgets?.rentcast} RC · {data.budgets?.autodev} AD</span>} sub="max external calls / day" testid="stat-budget" delay={0.14} />
          </div>

          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.18, duration: 0.4 }}
            className="mt-6 rounded-2xl border border-border/60 bg-card/60 p-6 backdrop-blur" data-testid="corpus-growth-chart">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <h2 className="text-lg font-medium">Corpus growth</h2>
                <div className="flex items-center rounded-full border border-border/60 p-0.5" data-testid="range-toggle">
                  {[30, 90].map((r) => (
                    <button
                      key={r}
                      onClick={() => setRange(r)}
                      data-testid={`range-${r}`}
                      className={`rounded-full px-3 py-1 text-xs transition-colors ${range === r ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:text-foreground"}`}
                    >
                      {r}d
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-4 text-xs text-muted-foreground">
                <span className="flex items-center gap-1.5"><span className="inline-block h-2 w-2 rounded-full bg-accent" /> Homes</span>
                <span className="flex items-center gap-1.5"><span className="inline-block h-2 w-2 rounded-full" style={{ background: "#7aa2f7" }} /> Vehicles</span>
              </div>
            </div>
            {history.length === 0 ? (
              <div className="flex h-56 items-center justify-center text-sm text-muted-foreground" data-testid="corpus-history-empty">
                Building history — the first daily snapshot appears after the next 6 AM warm.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={chartData} margin={{ top: 6, right: 8, left: -8, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gHomes" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="var(--accent, #c8a24a)" stopOpacity={0.5} />
                      <stop offset="95%" stopColor="var(--accent, #c8a24a)" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="gVeh" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#7aa2f7" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#7aa2f7" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(128,128,128,0.15)" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: "rgba(150,150,150,0.9)" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "rgba(150,150,150,0.9)" }} axisLine={false} tickLine={false} width={48} />
                  <Tooltip contentStyle={{ background: "rgba(20,20,26,0.95)", border: "1px solid rgba(128,128,128,0.3)", borderRadius: 10, fontSize: 12 }} />
                  <Area type="monotone" dataKey="homes" stroke="var(--accent, #c8a24a)" strokeWidth={2} fill="url(#gHomes)" />
                  <Area type="monotone" dataKey="vehicles" stroke="#7aa2f7" strokeWidth={2} fill="url(#gVeh)" />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.22, duration: 0.4 }}
            className="mt-6 rounded-2xl border border-border/60 bg-card/60 p-6 backdrop-blur" data-testid="veh-make-breakdown">
            <h2 className="mb-4 text-lg font-medium">Vehicles by make</h2>
            <div className="grid grid-cols-2 gap-x-6 gap-y-2 sm:grid-cols-3 md:grid-cols-4">
              {(data.vehicle_makes || []).map((m) => (
                <div key={m.make} className="flex items-center justify-between border-b border-border/40 py-1.5 text-sm" data-testid={`make-row-${m.make}`}>
                  <span className="text-muted-foreground">{m.make}</span>
                  <span className="font-mono-data tabular-nums">{nf(m.count)}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {fresh && (
            <motion.div
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.26, duration: 0.4 }}
              className="mt-6 rounded-2xl border border-border/60 bg-card/60 p-6 backdrop-blur" data-testid="freshness-panel">
              <div className="mb-1 flex items-center gap-2"><Clock className="h-4 w-4 text-accent" /><h2 className="text-lg font-medium">Reconfirmation health</h2></div>
              <p className="mb-4 text-xs text-muted-foreground">Oldest verified timestamp per scope (sorted stalest-first). Amber = listings not re-confirmed in the last {fresh.reconfirm_days} days (still cycling through the sweep). The daily sweep re-walks the whole corpus about every 2–3 days.</p>
              {fresh.re_status && !fresh.re_status.ok && (
                <div className="mb-4 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-400" data-testid="homes-refresh-error">
                  Homes refresh failed on {fresh.re_status.date} (attempt {fresh.re_status.attempts}, {fresh.re_status.used} RentCast calls used): {fresh.re_status.error || "unknown error"}. Automatic retry runs every 2h, up to 3 attempts/day, within the 60-call budget.
                </div>
              )}
              <div className="grid gap-6 md:grid-cols-2">
                {[["Homes · by state", fresh.homes, "homes"], ["Vehicles · by make", fresh.vehicles, "vehicles"]].map(([title, rows, k]) => (
                  <div key={title}>
                    <div className="mb-2 flex items-center justify-between font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground">
                      <span>{title}</span>
                      {fresh.summary?.[k] && (
                        <span className="text-emerald-500" data-testid={`admin-freshness-pct-${k}`}>
                          {fresh.summary[k].verified_pct}% verified · {nf(fresh.summary[k].fresh)} / {nf(fresh.summary[k].total)}
                        </span>
                      )}
                    </div>
                    <div className="max-h-80 overflow-y-auto rounded-lg border border-border/40 divide-y divide-border/30">
                      {(rows || []).length === 0 ? (
                        <div className="px-3 py-3 text-sm text-muted-foreground">No data.</div>
                      ) : (rows || []).map((r) => (
                        <div key={r.key} className="flex items-center justify-between gap-2 px-3 py-1.5 text-sm" data-testid={`freshness-row-${r.key}`}>
                          <span className="w-12 shrink-0 text-muted-foreground">{r.key}</span>
                          <span className="w-16 shrink-0 text-right font-mono-data text-xs tabular-nums">{nf(r.count)}</span>
                          <span className={`flex-1 text-right font-mono-data text-[11px] tabular-nums ${r.reconfirming > 0 ? "text-amber-500" : "text-emerald-500"}`}>{fmtDate(r.oldest)}</span>
                          <span className="w-16 shrink-0 text-right font-mono-data text-[10px] text-amber-500/80">{r.reconfirming > 0 ? `${nf(r.reconfirming)} stale` : ""}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </>
      )}
    </div>
  );
}
